import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Card, 
  Button, 
  Form, 
  Input, 
  Select, 
  Modal, 
  message, 
  Space, 
  Typography, 
  Tag,
  Popconfirm,
  Switch,
  Tooltip,
  InputNumber,
  DatePicker,
  Divider,
  Row,
  Col,
  Alert,
  Collapse,
  Checkbox,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { taxRulesService } from '../../services/taxRulesService';
import { taxCategoriesService } from '../../services/taxCategoriesService';
import { jurisdictionsService } from '../../services/jurisdictionsService';
import './TaxRules.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// Calculation methods
const CALCULATION_METHODS = [
  { label: 'Specific (Per Unit)', value: 'specific' },
  { label: 'Ad Valorem (Percentage)', value: 'ad_valorem' }
];

// Application points
const APPLICATION_POINTS = [
  { label: 'Manufacturer', value: 'manufacturer' },
  { label: 'Wholesale', value: 'wholesale' },
  { label: 'Retail', value: 'retail' }
];

// Display methods
const DISPLAY_METHODS = [
  { label: 'Included in Price', value: 'included' },
  { label: 'Show Separately', value: 'separate' },
  { label: 'Show Both Ways', value: 'both' }
];

// Shipping tax methods
const SHIPPING_TAX_METHODS = [
  { label: 'Destination Based', value: 'destination_based' },
  { label: 'Origin Based', value: 'origin_based' }
];

// Calculation order methods (how excise and sales tax interact)
const CALCULATION_ORDERS = [
  { 
    label: 'Excise First, Then Sales Tax', 
    value: 'excise_then_sales',
    description: 'Most common: Apply excise tax first, then sales tax on total including excise'
  },
  { 
    label: 'Excise Separate from Sales Tax', 
    value: 'excise_separate',
    description: 'Calculate excise and sales taxes separately on base price'
  },
  { 
    label: 'Excise Only (No Sales Tax)', 
    value: 'excise_only',
    description: 'Only apply excise tax, no sales tax calculation'
  }
];

// Condition operators (for future use)
// const CONDITION_OPERATORS = [
//   { label: '>', value: '>' },
//   { label: '<', value: '<' },
//   { label: '>=', value: '>=' },
//   { label: '<=', value: '<=' },
//   { label: '==', value: '==' },
//   { label: '!=', value: '!=' }
// ];

// Unit measurement types
const UNIT_TYPES = [
  // Volume
  { label: 'Milliliter (ml)', value: 'ml', category: 'volume' },
  { label: 'Liter (L)', value: 'liter', category: 'volume' },
  { label: 'Gallon', value: 'gallon', category: 'volume' },
  { label: 'Fluid Ounce (fl oz)', value: 'fl_oz', category: 'volume' },
  { label: 'Barrel', value: 'barrel', category: 'volume' },
  
  // Weight
  { label: 'Gram (g)', value: 'gram', category: 'weight' },
  { label: 'Kilogram (kg)', value: 'kg', category: 'weight' },
  { label: 'Pound (lb)', value: 'pound', category: 'weight' },
  { label: 'Ounce (oz)', value: 'ounce', category: 'weight' },
  
  // Count/Package
  { label: 'Each', value: 'each', category: 'count' },
  { label: 'Pack', value: 'pack', category: 'count' },
  { label: 'Carton', value: 'carton', category: 'count' },
  { label: 'Case', value: 'case', category: 'count' },
  { label: 'Stick', value: 'stick', category: 'count' },
  { label: 'Bottle', value: 'bottle', category: 'count' }
];


function ExciseTaxRules() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [jurisdictionData, setJurisdictionData] = useState({
    countries: [],
    states: [],
    counties: [],
    cities: []
  });
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [calculationPreview, setCalculationPreview] = useState(null);
  const [jurisdictionScope, setJurisdictionScope] = useState('state');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRules();
    fetchCategories();
    fetchJurisdictions();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const rulesData = await taxRulesService.getAll();
      setRules(rulesData);
    } catch (error) {
      console.error('Error fetching excise tax rules:', error);
      message.error('Failed to fetch excise tax rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesData = await taxCategoriesService.getAll();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to fetch tax categories');
    }
  };

  const fetchJurisdictions = async () => {
    try {
      // Fetch countries (should be just US for now)
      const countries = await jurisdictionsService.getCountriesList();
      
      // Fetch states for US
      const states = await jurisdictionsService.getStatesByCountry('US');
      
      setJurisdictionData({
        countries: countries.map(country => ({ code: country, name: country === 'US' ? 'United States' : country })),
        states: states,
        counties: [],
        cities: []
      });
    } catch (error) {
      console.error('Error fetching jurisdictions:', error);
      message.error('Failed to fetch jurisdiction data');
    }
  };

  const handleSaveRule = async (values) => {
    try {
      // Combine unitQuantity and unitType into single unit field for storage
      const combinedUnit = `${values.unitQuantity || 1}${values.unitType || 'each'}`;
      
      const ruleData = {
        ...values,
        unit: combinedUnit, // Store as combined unit (e.g., "20ml", "1pack")
        id: isEditing ? selectedRule.id : generateRuleId(values.name),
        createdAt: isEditing ? selectedRule.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin@lithlan.com' // TODO: Get from auth context
      };
      
      // Remove the separate unitQuantity and unitType fields from storage
      delete ruleData.unitQuantity;
      delete ruleData.unitType;

      if (isEditing) {
        await taxRulesService.updateRule(ruleData.id, ruleData);
      } else {
        await taxRulesService.createRule(ruleData);
      }

      message.success(`Excise tax rule ${isEditing ? 'updated' : 'created'} successfully`);
      setEditModalVisible(false);
      form.resetFields();
      setSelectedRule(null);
      setIsEditing(false);
      setCalculationPreview(null);
      fetchRules();

    } catch (error) {
      console.error('Error saving rule:', error);
      message.error(`Failed to ${isEditing ? 'update' : 'create'} excise tax rule`);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await taxRulesService.archiveRule(ruleId);
      message.success('Excise tax rule archived successfully');
      fetchRules();
    } catch (error) {
      console.error('Error deleting rule:', error);
      message.error('Failed to archive excise tax rule');
    }
  };

  const generateRuleId = (name) => {
    return `excise_${name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setIsEditing(true);
    setEditModalVisible(true);
    
    // Determine jurisdiction scope based on rule data
    let scope = 'federal';
    if (rule.jurisdiction?.zipCodes?.length > 0) {
      scope = 'zip';
    } else if (rule.jurisdiction?.city) {
      scope = 'city';
    } else if (rule.jurisdiction?.county) {
      scope = 'county';
    } else if (rule.jurisdiction?.state) {
      scope = 'state';
    }
    
    setJurisdictionScope(scope);
    
    // Parse existing unit format (e.g., "pack", "20ml", "1gallon") into quantity + type
    let unitQuantity = 1;
    let unitType = 'each';
    
    if (rule.unit) {
      const unitMatch = rule.unit.match(/^(\d*\.?\d*)\s*(.+)$/);
      if (unitMatch) {
        unitQuantity = parseFloat(unitMatch[1]) || 1;
        unitType = unitMatch[2].toLowerCase();
        // Map common variations to our standard values
        if (unitType === 'ml') unitType = 'ml';
        else if (unitType === 'pack') unitType = 'pack';
        else if (unitType === 'gallon') unitType = 'gallon';
        else if (unitType === 'barrel') unitType = 'barrel';
        // Add more mappings as needed
      } else {
        // If no number prefix, assume quantity is 1
        unitType = rule.unit.toLowerCase();
      }
    }
    
    form.setFieldsValue({
      name: rule.name,
      description: rule.description,
      taxCategoryId: rule.taxCategoryId,
      
      // Geographic targeting with scope
      jurisdictionScope: scope,
      'jurisdiction.country': rule.jurisdiction.country,
      'jurisdiction.state': rule.jurisdiction.state,
      'jurisdiction.county': rule.jurisdiction.county,
      'jurisdiction.city': rule.jurisdiction.city,
      'jurisdiction.zipCodes': rule.jurisdiction.zipCodes || [],
      'jurisdiction.excludeZipCodes': rule.jurisdiction.excludeZipCodes || [],
      
      // Tax calculation
      calculationMethod: rule.calculationMethod,
      rate: rule.rate,
      unitQuantity: unitQuantity,
      unitType: unitType,
      applicationPoint: rule.applicationPoint,
      applyBeforeDiscounts: rule.applyBeforeDiscounts,
      includeInSalesTaxBase: rule.includeInSalesTaxBase,
      
      // Shipping treatment
      'shippingTreatment.applyToShipping': rule.shippingTreatment?.applyToShipping,
      'shippingTreatment.mustPrepay': rule.shippingTreatment?.mustPrepay,
      'shippingTreatment.requiresStamps': rule.shippingTreatment?.requiresStamps,
      'shippingTreatment.shippingTaxMethod': rule.shippingTreatment?.shippingTaxMethod,
      
      // Display and metadata
      displayMethod: rule.displayMethod,
      effectiveDate: rule.effectiveDate ? dayjs(rule.effectiveDate) : null,
      expirationDate: rule.expirationDate ? dayjs(rule.expirationDate) : null,
      isActive: rule.isActive,
      priority: rule.priority
    });
    
    // Calculate preview for current rule
    updateCalculationPreview(rule);
  };

  const handleAddRule = () => {
    setSelectedRule(null);
    setIsEditing(false);
    setEditModalVisible(true);
    setCalculationPreview(null);
    setJurisdictionScope('state'); // Set default scope to state
    form.resetFields();
    form.setFieldsValue({
      // Geographic defaults - State level as default
      jurisdictionScope: 'state',
      'jurisdiction.country': 'US',
      'jurisdiction.state': null,
      'jurisdiction.county': null,
      'jurisdiction.city': null,
      'jurisdiction.zipCodes': [],
      'jurisdiction.excludeZipCodes': [],
      
      // Tax calculation defaults
      calculationMethod: 'specific',
      unitQuantity: 1,
      unitType: 'each',
      applicationPoint: 'retail',
      applyBeforeDiscounts: true,
      includeInSalesTaxBase: true,
      displayMethod: 'included',
      
      // Shipping defaults
      'shippingTreatment.applyToShipping': false,
      'shippingTreatment.mustPrepay': false,
      'shippingTreatment.requiresStamps': false,
      'shippingTreatment.shippingTaxMethod': 'destination_based',
      
      // Status defaults
      isActive: true,
      priority: 100
    });
  };

  // Real-time calculation preview
  const updateCalculationPreview = (formValues) => {
    try {
      const category = categories.find(cat => cat.id === formValues.taxCategoryId);
      if (!category || !formValues.rate || !formValues.unitQuantity || !formValues.unitType) {
        setCalculationPreview(null);
        return;
      }

      let preview = null;
      const unitQuantity = formValues.unitQuantity || 1;
      const unitType = formValues.unitType || 'each';
      const unitLabel = `${unitQuantity}${unitType}`;
      
      if (formValues.calculationMethod === 'specific') {
        // Calculate tax per the specified unit (e.g., per 20ml, not per 1ml)
        const taxPerUnit = formValues.rate; // This is the tax per unitQuantity of unitType
        const examples = [];
        
        // Show examples in multiples of the entered unit
        const multipliers = [1, 2, 5, 10];
        
        multipliers.forEach(multiplier => {
          const totalQuantity = unitQuantity * multiplier;
          const totalTax = taxPerUnit * multiplier;
          
          examples.push(
            `${totalQuantity}${unitType} (${multiplier} × ${unitLabel}) = $${totalTax.toFixed(2)} excise tax`
          );
        });
        
        // Add category-specific examples if helpful
        if (category.id === 'TOBACCO_CIGARETTES' && unitType === 'pack') {
          examples.push(`1 carton (10 packs) = $${(10 * taxPerUnit).toFixed(2)} excise tax`);
        } else if (category.id === 'ALCOHOL_BEER' && unitType === 'gallon') {
          examples.push(`1 barrel (31 gallons) = $${(31 * taxPerUnit).toFixed(2)} excise tax`);
        }
        
        preview = {
          method: 'Specific (Per Unit)',
          rate: `$${formValues.rate} per ${unitLabel}`,
          examples: examples.slice(0, 4) // Limit to 4 examples
        };
      } else if (formValues.calculationMethod === 'ad_valorem') {
        // Examples for ad valorem (percentage) rates
        const examples = [
          `$100 product = $${(100 * (formValues.rate / 100)).toFixed(2)} excise tax (${formValues.rate}%)`,
          `$250 product = $${(250 * (formValues.rate / 100)).toFixed(2)} excise tax`,
          `$500 product = $${(500 * (formValues.rate / 100)).toFixed(2)} excise tax`
        ];
        
        preview = {
          method: 'Ad Valorem (Percentage)',
          rate: `${formValues.rate}% of value`,
          examples
        };
      }
      
      setCalculationPreview(preview);
    } catch (error) {
      console.error('Error calculating preview:', error);
      setCalculationPreview(null);
    }
  };

  // Helper function to get display name for units
  const handleFormChange = () => {
    try {
      const values = form.getFieldsValue();
      updateCalculationPreview(values);
    } catch (error) {
      // Form validation failed - this is normal during typing
    }
  };

  const columns = [
    {
      title: 'Rule Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.taxCategoryPath}
          </Text>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Jurisdiction',
      dataIndex: 'jurisdiction',
      key: 'jurisdiction',
      render: (jurisdiction) => {
        const parts = [];
        if (jurisdiction.country) parts.push(jurisdiction.country);
        if (jurisdiction.state) parts.push(jurisdiction.state);
        if (jurisdiction.county) parts.push(jurisdiction.county);
        if (jurisdiction.city) parts.push(jurisdiction.city);
        return parts.join(' > ') || 'All Jurisdictions';
      }
    },
    {
      title: 'Tax Calculation',
      key: 'calculation',
      render: (record) => (
        <div>
          <Tag color={record.calculationMethod === 'specific' ? 'blue' : 'green'}>
            {record.calculationMethod === 'specific' ? 'Specific' : 'Ad Valorem'}
          </Tag>
          <br />
          <Text>
            {record.calculationMethod === 'specific' 
              ? `$${record.rate} per ${record.unit}`
              : `${record.rate}%`
            }
          </Text>
        </div>
      )
    },
    {
      title: 'Application Point',
      dataIndex: 'applicationPoint',
      key: 'applicationPoint',
      render: (point) => (
        <Tag color="purple">
          {point?.charAt(0).toUpperCase() + point?.slice(1)}
        </Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (record) => (
        <div>
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Priority: {record.priority}
          </Text>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Space>
          <Tooltip title="Edit Rule">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditRule(record)}
            />
          </Tooltip>
          <Tooltip title="Archive Rule">
            <Popconfirm
              title="Are you sure you want to archive this rule?"
              onConfirm={() => handleDeleteRule(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  const filteredRules = rules.filter(rule => {
    if (filterValues.category && rule.taxCategoryId !== filterValues.category) return false;
    if (filterValues.jurisdiction && rule.jurisdiction?.state !== filterValues.jurisdiction) return false;
    if (filterValues.status !== undefined && rule.isActive !== filterValues.status) return false;
    return true;
  });

  return (
    <div className="excise-tax-rules">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>Excise Tax Rules Management</Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddRule}
            >
              Add Excise Tax Rule
            </Button>
          </div>
        }
      >
        {/* Filter Bar */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Select
                placeholder="Filter by Category"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => setFilterValues(prev => ({ ...prev, category: value }))}
              >
                {categories.map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.pathString}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Filter by State"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => setFilterValues(prev => ({ ...prev, jurisdiction: value }))}
              >
                {jurisdictionData.states.US?.map(state => (
                  <Option key={state.code} value={state.code}>
                    {state.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Filter by Status"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => setFilterValues(prev => ({ ...prev, status: value }))}
              >
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </Col>
            <Col span={6}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchRules}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Text type="secondary">
                  {filteredRules.length} of {rules.length} rules
                </Text>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={filteredRules}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rules`
          }}
        />
      </Card>

      {/* Add/Edit Rule Modal */}
      <Modal
        title={isEditing ? 'Edit Excise Tax Rule' : 'Add New Excise Tax Rule'}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
          setCalculationPreview(null);
        }}
        onOk={() => form.submit()}
        width={1000}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveRule}
          onFieldsChange={handleFormChange}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Rule Name"
                name="name"
                rules={[{ required: true, message: 'Please enter rule name' }]}
              >
                <Input placeholder="Enter excise tax rule name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Tax Category"
                name="taxCategoryId"
                rules={[{ required: true, message: 'Please select a tax category' }]}
              >
                <Select placeholder="Select category from taxonomy">
                  {categories.map(category => (
                    <Option key={category.id} value={category.id}>
                      {category.pathString}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={2}
              placeholder="Describe this excise tax rule and when it applies"
            />
          </Form.Item>

          <Divider orientation="left">Geographic Targeting</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Jurisdiction Scope"
                name="jurisdictionScope"
                rules={[{ required: true, message: 'Please select jurisdiction scope' }]}
                help="Determines the geographic range of this tax rule"
              >
                <Select 
                  placeholder="Select jurisdiction scope"
                  onChange={(value) => {
                    setJurisdictionScope(value);
                    // Always keep country as US, clear dependent fields
                    form.setFieldsValue({
                      'jurisdiction.country': 'US',
                      'jurisdiction.state': null,
                      'jurisdiction.county': null,
                      'jurisdiction.city': null,
                      'jurisdiction.zipCodes': [],
                      'jurisdiction.excludeZipCodes': []
                    });
                  }}
                >
                  <Option value="federal">Federal (All US)</Option>
                  <Option value="state">State Level</Option>
                  <Option value="county">County Level</Option>
                  <Option value="city">City Level</Option>
                  <Option value="zip">ZIP Code Level</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Country"
                name={['jurisdiction', 'country']}
                initialValue="US"
                rules={[{ required: true, message: 'Please select country' }]}
              >
                <Select placeholder="Select country" disabled>
                  {jurisdictionData.countries.map(country => (
                    <Option key={country.code} value={country.code}>
                      {country.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Priority"
                name="priority"
                rules={[{ required: true, message: 'Please enter priority' }]}
                help="Higher numbers = higher priority"
              >
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* State Level Selection */}
          {jurisdictionScope !== 'federal' && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="State"
                  name={['jurisdiction', 'state']}
                  rules={
                    ['state', 'county', 'city', 'zip'].includes(jurisdictionScope)
                      ? [{ required: true, message: 'Please select state' }]
                      : []
                  }
                >
                  <Select 
                    placeholder="Select state" 
                    allowClear
                    onChange={async (value) => {
                      // Reset dependent fields when state changes
                      form.setFieldsValue({
                        'jurisdiction.county': null,
                        'jurisdiction.city': null
                      });
                      
                      // Fetch counties for selected state
                      if (value) {
                        try {
                          const counties = await jurisdictionsService.getCountiesByState('US', value);
                          setJurisdictionData(prev => ({
                            ...prev,
                            counties: counties
                          }));
                        } catch (error) {
                          console.error('Error fetching counties:', error);
                        }
                      }
                    }}
                  >
                    {jurisdictionData.states.map(state => (
                      <Option key={state.state} value={state.state}>
                        {state.state}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Alert
                  message={
                    jurisdictionScope === 'state' 
                      ? "State-wide tax rule: Applies to entire state unless overridden by county/city rules"
                      : "Select state first to enable county and city options"
                  }
                  type="info"
                  showIcon
                  style={{ height: 'fit-content', marginTop: '30px' }}
                />
              </Col>
            </Row>
          )}

          {/* County Level Selection */}
          {['county', 'city', 'zip'].includes(jurisdictionScope) && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="County"
                  name={['jurisdiction', 'county']}
                  rules={
                    ['county', 'city'].includes(jurisdictionScope)
                      ? [{ required: true, message: 'Please select county' }]
                      : []
                  }
                >
                  <Select 
                    placeholder="Select county" 
                    allowClear
                    disabled={!form.getFieldValue(['jurisdiction', 'state'])}
                    onChange={async (value) => {
                      // Reset city when county changes
                      form.setFieldsValue({
                        'jurisdiction.city': null
                      });
                      
                      // Fetch cities for selected county
                      if (value) {
                        try {
                          const state = form.getFieldValue(['jurisdiction', 'state']);
                          const cities = await jurisdictionsService.getCitiesByCounty('US', state, value);
                          setJurisdictionData(prev => ({
                            ...prev,
                            cities: cities
                          }));
                        } catch (error) {
                          console.error('Error fetching cities:', error);
                        }
                      }
                    }}
                  >
                    {jurisdictionData.counties.map(county => (
                      <Option key={county.county} value={county.county}>
                        {county.county}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Alert
                  message={
                    jurisdictionScope === 'county'
                      ? "County-wide tax rule: Applies to entire county unless overridden by city rules"
                      : "County selection helps narrow down city options"
                  }
                  type="info"
                  showIcon
                  style={{ height: 'fit-content', marginTop: '30px' }}
                />
              </Col>
            </Row>
          )}

          {/* City Level Selection */}
          {['city', 'zip'].includes(jurisdictionScope) && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="City"
                  name={['jurisdiction', 'city']}
                  rules={
                    jurisdictionScope === 'city'
                      ? [{ required: true, message: 'Please select city' }]
                      : []
                  }
                >
                  <Select 
                    placeholder="Select city" 
                    allowClear
                    disabled={!form.getFieldValue(['jurisdiction', 'county'])}
                  >
                    {jurisdictionData.cities.map(city => (
                      <Option key={city.city} value={city.city}>
                        {city.city}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Alert
                  message="City-specific tax rule: Applies only to the selected city"
                  type="info"
                  showIcon
                  style={{ height: 'fit-content', marginTop: '30px' }}
                />
              </Col>
            </Row>
          )}

          {/* ZIP Code Level Selection */}
          {jurisdictionScope === 'zip' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="ZIP Codes"
                  name={['jurisdiction', 'zipCodes']}
                  rules={[{ required: true, message: 'Please enter at least one ZIP code' }]}
                  help="Enter ZIP codes separated by commas (e.g., 90210, 90211, 90212)"
                >
                  <Select
                    mode="tags"
                    placeholder="Enter ZIP codes"
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Exclude ZIP Codes"
                  name={['jurisdiction', 'excludeZipCodes']}
                  help="ZIP codes to exclude from this rule (optional)"
                >
                  <Select
                    mode="tags"
                    placeholder="Enter ZIP codes to exclude"
                    style={{ width: '100%' }}
                    tokenSeparators={[',']}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Divider orientation="left">Excise Tax Configuration</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Calculation Method"
                name="calculationMethod"
                rules={[{ required: true, message: 'Please select calculation method' }]}
              >
                <Radio.Group>
                  {CALCULATION_METHODS.map(method => (
                    <Radio key={method.value} value={method.value}>
                      {method.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Tax Rate"
                name="rate"
                rules={[{ required: true, message: 'Please enter tax rate' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }}
                  addonAfter={form.getFieldValue('calculationMethod') === 'ad_valorem' ? '%' : '$'}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Unit Quantity"
                name="unitQuantity"
                help="Amount per unit"
                rules={[{ required: true, message: 'Enter quantity' }]}
              >
                <InputNumber
                  min={0.01}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="1"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Unit Type"
                name="unitType"
                help="Measurement unit"
                rules={[{ required: true, message: 'Select unit type' }]}
              >
                <Select placeholder="Select unit">
                  {UNIT_TYPES.map(unit => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Application Point"
                name="applicationPoint"
                rules={[{ required: true, message: 'Please select application point' }]}
              >
                <Select placeholder="Select application point">
                  {APPLICATION_POINTS.map(point => (
                    <Option key={point.value} value={point.value}>
                      {point.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Display Method"
                name="displayMethod"
                rules={[{ required: true, message: 'Please select display method' }]}
              >
                <Select placeholder="Select display method">
                  {DISPLAY_METHODS.map(method => (
                    <Option key={method.value} value={method.value}>
                      {method.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Tax Application">
                <div>
                  <Form.Item
                    name="applyBeforeDiscounts"
                    valuePropName="checked"
                    style={{ marginBottom: 8 }}
                  >
                    <Checkbox>Apply before discounts</Checkbox>
                  </Form.Item>
                  <Form.Item
                    name="includeInSalesTaxBase"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>Include in sales tax base</Checkbox>
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Shipping Treatment</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Shipping Tax Configuration">
                <div>
                  <Form.Item
                    name={['shippingTreatment', 'applyToShipping']}
                    valuePropName="checked"
                    style={{ marginBottom: 8 }}
                  >
                    <Checkbox>Apply excise tax to shipping costs</Checkbox>
                  </Form.Item>
                  <Form.Item
                    name={['shippingTreatment', 'mustPrepay']}
                    valuePropName="checked"
                    style={{ marginBottom: 8 }}
                  >
                    <Checkbox>Must prepay tax (tobacco-specific)</Checkbox>
                  </Form.Item>
                  <Form.Item
                    name={['shippingTreatment', 'requiresStamps']}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>Requires tax stamps (tobacco-specific)</Checkbox>
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Shipping Tax Method"
                name={['shippingTreatment', 'shippingTaxMethod']}
              >
                <Select placeholder="Select shipping tax method">
                  {SHIPPING_TAX_METHODS.map(method => (
                    <Option key={method.value} value={method.value}>
                      {method.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Tax Application Settings</Divider>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Calculation Order"
                name="calculationOrder"
                help="How should this excise tax interact with sales tax?"
                rules={[{ required: true, message: 'Please select calculation order' }]}
              >
                <Radio.Group>
                  {CALCULATION_ORDERS.map(order => (
                    <Radio key={order.value} value={order.value} style={{ display: 'block', marginBottom: 8 }}>
                      <div>
                        <strong>{order.label}</strong>
                        <div style={{ color: '#666', fontSize: '12px', marginTop: 2 }}>
                          {order.description}
                        </div>
                      </div>
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Effective Dates & Status</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Effective Date"
                name="effectiveDate"
                rules={[{ required: true, message: 'Please select effective date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Expiration Date"
                name="expirationDate"
                help="Leave empty for no expiration"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Status"
                name="isActive"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          {/* Real-time Calculation Preview */}
          {calculationPreview && (
            <Collapse style={{ marginTop: 16 }}>
              <Panel 
                header={
                  <div>
                    <CalculatorOutlined style={{ marginRight: 8 }} />
                    Live Tax Calculation Preview
                  </div>
                } 
                key="preview"
              >
                <Alert
                  message={`${calculationPreview.method}: ${calculationPreview.rate}`}
                  description={
                    <div>
                      <Text strong>Examples:</Text>
                      <ul style={{ marginTop: 8, marginBottom: 0 }}>
                        {calculationPreview.examples.map((example, index) => (
                          <li key={index} style={{ marginBottom: 4 }}>
                            <code>{example}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                  type="info"
                  showIcon
                />
              </Panel>
            </Collapse>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default ExciseTaxRules;